import React, { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faSave } from "@fortawesome/free-solid-svg-icons"
import { useSelector } from "react-redux"
import { selectUser } from "../../../../redux/store/auth/authSlice"
import { Note } from "../../../../types/brandDatas"

interface NotesModalProps {
  isOpen: boolean
  onClose: () => void
  brandId: string
  notes: Note[]
  campaignNotes?: any[]
  onAddNote: (
    brandId: string,
    note?: string,
    files?: FileList | File[],
  ) => Promise<Note[] | undefined>

  onDeleteNote: (brandId: string, index: number) => void
}

const NotesModal: React.FC<NotesModalProps> = ({
  isOpen,
  onClose,
  brandId,
  notes,
  campaignNotes = [],
  onAddNote,
  onDeleteNote,
}) => {
  const [newNote, setNewNote] = useState("")
  const [localNotes, setLocalNotes] = useState<Note[]>([])
  const [activeTab, setActiveTab] = useState<"brand" | "campaign">("brand")
  const [isSaving, setIsSaving] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""

  useEffect(() => {
    if (isOpen) {
      setLocalNotes((notes as Note[]) || [])
      setNewNote("")
      setSelectedFiles([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }, [isOpen, notes])

  const brandNoteCount =
    localNotes?.filter((n) => (n.note && n.note !== "") || (n.attachments?.length ?? 0) > 0)
      .length || 0

  const handleAddNote = async () => {
    const hasText = newNote.trim().length > 0
    const hasFiles = selectedFiles.length > 0
    if (!hasText && !hasFiles) {
      return
    }

    const optimisticAttachments =
      selectedFiles.length > 0 ? selectedFiles.map((f) => ({ file_name: f.name })) : []
    const newNoteObj: Note = {
      note: newNote.trim(),
      author: userEmail,
      note_time: new Date(),
      attachments: optimisticAttachments,
    }
    setLocalNotes((prev) => [newNoteObj, ...prev])

    setIsSaving(true)
    try {
      const serverNotes = await onAddNote(
        brandId,
        hasText ? newNote.trim() : undefined,
        selectedFiles.length ? selectedFiles : undefined,
      )

      if (serverNotes) {
        setLocalNotes(serverNotes)
      }
      setNewNote("")
      setSelectedFiles([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleAddNote()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Notlar</h2>
              <p className="text-sm opacity-90 mt-1">Marka ID: {brandId}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              ×
            </button>
          </div>

          <div className="flex space-x-4 mt-4">
            <button
              onClick={() => setActiveTab("brand")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "brand"
                  ? "bg-white text-blue-600"
                  : "bg-blue-500 text-white hover:bg-blue-400"
              }`}
            >
              Marka Notları ({brandNoteCount})
            </button>
            <button
              onClick={() => setActiveTab("campaign")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "campaign"
                  ? "bg-white text-blue-600"
                  : "bg-blue-500 text-white hover:bg-blue-400"
              }`}
            >
              Kampanya Notları ({campaignNotes?.length || 0})
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto">
          <div className="mb-6">
            {activeTab === "brand" ? (
              localNotes && localNotes.length > 0 ? (
                <div className="space-y-3">
                  {localNotes
                    .filter(
                      (note) =>
                        (note.note && note.note !== "") || (note.attachments?.length ?? 0) > 0,
                    )
                    .map((note, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            {note.note && note.note !== "" && (
                              <p className="text-gray-800 text-sm mb-2">{note.note}</p>
                            )}
                            {note.attachments && note.attachments.length > 0 && (
                              <ul className="mt-1 space-y-1">
                                {note.attachments.map((att, i) => {
                                  const label =
                                    att.file_name ||
                                    (att.mime_type?.includes("pdf")
                                      ? "PDF"
                                      : att.mime_type?.includes("word")
                                      ? "DOCX"
                                      : "Dosya")
                                  return (
                                    <li key={i} className="text-xs">
                                      {att.file_url ? (
                                        <a
                                          href={att.file_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-600 hover:underline break-all"
                                        >
                                          {label}
                                        </a>
                                      ) : (
                                        <span className="text-gray-600">
                                          {label} <em className="text-gray-400">(yükleniyor…)</em>
                                        </span>
                                      )}
                                      {att.file_size ? (
                                        <span className="text-gray-400 ml-1">
                                          ({Math.round(att.file_size / 1024)} KB)
                                        </span>
                                      ) : null}
                                    </li>
                                  )
                                })}
                              </ul>
                            )}

                            <div className="flex items-center text-xs text-gray-500 mt-2">
                              <span className="font-semibold">{note.author}</span>
                              <span className="mx-1">•</span>
                              <span>{new Date(note.note_time).toLocaleString("tr-TR")}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              onDeleteNote(brandId, index)
                              setLocalNotes((prev) => prev.filter((_, i) => i !== index))
                            }}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Henüz not eklenmemiş</p>
                </div>
              )
            ) : campaignNotes && campaignNotes.length > 0 ? (
              <div className="space-y-4">
                {campaignNotes.map((campaign, campaignIndex) => (
                  <div key={campaignIndex} className="border rounded-lg p-3 bg-blue-50">
                    <h4 className="font-semibold text-blue-800 mb-2">{campaign.name}</h4>
                    {campaign.notes && campaign.notes.length > 0 ? (
                      <div className="space-y-2">
                        {campaign.notes.map((note: any, noteIndex: number) => (
                          <div key={noteIndex} className="bg-white rounded p-2">
                            <p className="text-gray-800 text-sm mb-1">{note.note}</p>
                            <div className="flex items-center text-xs text-gray-500">
                              <span className="font-semibold">{note.author}</span>
                              <span className="mx-1">•</span>
                              <span>{new Date(note.note_time).toLocaleString("tr-TR")}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Bu kampanya için not bulunmuyor</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Kampanya notu bulunmuyor</p>
              </div>
            )}
          </div>

          {activeTab === "brand" && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Yeni Not Ekle</h3>
              <div className="flex flex-col gap-2">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Notunuzu buraya yazın (opsiyonel). Sadece dosya da ekleyebilirsiniz."
                  className="w-full p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />

                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) =>
                      setSelectedFiles(e.target.files ? Array.from(e.target.files) : [])
                    }
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                {selectedFiles.length > 0 && (
                  <div className="text-xs text-gray-600">
                    Seçilen dosyalar: {selectedFiles.map((f) => f.name).join(", ")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {activeTab === "brand" ? (
                <>Toplam: {brandNoteCount} marka notu</>
              ) : (
                <>
                  Toplam: {campaignNotes?.length || 0} kampanya,{" "}
                  {campaignNotes?.reduce(
                    (total, campaign) => total + (campaign.notes?.length || 0),
                    0,
                  ) || 0}{" "}
                  not
                </>
              )}
            </div>
            <button
              onClick={handleAddNote}
              disabled={(!newNote.trim() && selectedFiles.length === 0) || isSaving}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{isSaving ? "Kaydediliyor..." : "Kaydet"}</span>
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotesModal
